import { DateFormatOptions } from 'shared/types/date-format-options';

export const dateInputSeparator = '-';

export type MaskedDateInput = {
  value: string;
  caret: number;
};

const separatorChars = [dateInputSeparator, '/', '.', ' '];

const isDigit = (char: string) => char >= '0' && char <= '9';

// Every separator advances a segment, even one closing an emptied one ("20--2026" keeps its year).
const toSegments = (value: string, partLengths: number[]): string[] => {
  const segments = partLengths.map(() => '');
  let index = 0;
  let hasDigits = false;

  for (const char of value) {
    if (isDigit(char)) {
      if (segments[index].length === partLengths[index]) {
        index += 1;
        if (index === partLengths.length) {
          break;
        }
      }

      segments[index] += char;
      hasDigits = true;
      continue;
    }

    if (!separatorChars.includes(char) || !hasDigits) {
      continue;
    }

    index += 1;
    if (index === partLengths.length) {
      break;
    }
  }

  return segments.slice(0, index + 1);
};

// Half-typed segments are never padded here ("00" is no month); parseDateInput pads at commit.
export const formatDateInput = (
  value: string,
  dateFormat: DateFormatOptions,
  caretIndex = value.length,
): MaskedDateInput => {
  const partLengths = dateFormat
    .split(dateInputSeparator)
    .map((part) => part.length);

  const segments = toSegments(value, partLengths);
  const beforeCaret = toSegments(value.slice(0, caretIndex), partLengths);

  const caretSegment = Math.min(beforeCaret.length, segments.length) - 1;
  const precedingSegments = segments
    .slice(0, caretSegment)
    .reduce((total, segment) => total + segment.length + 1, 0);

  return {
    value: segments.join(dateInputSeparator),
    caret: precedingSegments + beforeCaret[caretSegment].length,
  };
};
